import { colors, screen } from 'components/styles/variables';
import { useTranslation } from 'react-i18next';
import clockIcon from 'public/static/images/vdp/clock.svg';
import { RealTimePriceChange } from 'lib/Presenters/BuyerIntelligencePresenter/types';
import PriceHistoryList from 'components/Vdp/PriceHistory/List';
import { useState } from 'react';
import * as _ from 'lodash-es';
import Spacer from 'components/Shared/Spacer';
import LinkButton from 'components/Shared/LinkButton';
import css from 'styled-jsx/css';

interface PriceHistoryProps {
  timeOnMarket: number | null;
  totalPriceChange: number | null;
  realTimePriceChanges: RealTimePriceChange[];
}

const linkButtonCss = css.resolve`
  margin: 24px 0px;
  align-self: flex-start;
`;

const PriceHistory = ({
  timeOnMarket,
  realTimePriceChanges,
  totalPriceChange,
}: PriceHistoryProps): JSX.Element => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  let priceChangeClass;
  let plusMinus;
  if ((totalPriceChange as number) < 0) {
    priceChangeClass = 'green';
    plusMinus = '-';
  } else if ((totalPriceChange as number) > 0) {
    priceChangeClass = 'red';
    plusMinus = '+';
  } else {
    priceChangeClass = 'black';
    plusMinus = '';
  }

  return (
    <div className="price-history">
      <div className="flex-container">
        <h2 className="price-history-subtitle">{t('vdp.priceHistory')}</h2>
        {!_.isNil(timeOnMarket) && (
          <div className="days-flex-container" data-testid="time-on-market">
            {timeOnMarket === 0 ? (
              <div className="new-listing">{t('vdp.newListing')}</div>
            ) : (
              <>
                <div className="clock-icon" />
                <div className="days">{timeOnMarket}</div>
                <div className="on-market-text">{t('vdp.daysOnMarket')}</div>
              </>
            )}
          </div>
        )}
      </div>
      {!_.isNil(totalPriceChange) && realTimePriceChanges.length > 0 && (
        <h3 className="total-price-change">
          {`${t('vdp.totalPriceChange')}: `}
          <span
            className={priceChangeClass}
            data-testid="total-price-change"
          >{`${plusMinus}$${Math.abs(totalPriceChange)}`}</span>
        </h3>
      )}

      <div className="price-history-timeline">
        {realTimePriceChanges.length > 0 && <div className="timeline"></div>}
        <PriceHistoryList expanded={expanded} realTimePriceChanges={realTimePriceChanges} />
      </div>

      {realTimePriceChanges.length > 5 ? (
        <LinkButton
          className={linkButtonCss.className}
          onClick={(): void => setExpanded(!expanded)}
        >
          {expanded ? t('vdp.showLessPriceHistory') : t('vdp.showAllPriceHistory')}
        </LinkButton>
      ) : (
        <Spacer size="24px" smSize="40px" />
      )}
      <style jsx>{`
        .price-history {
          width: 100%;
          display: flex;
          flex-direction: column;
        }

        .flex-container {
          display: flex;
          justify-content: space-between;
          margin: 20px 0px 0px 0px;
          align-items: center;
        }

        .price-history-subtitle {
          margin: 0;
          font-size: 18px;
          font-weight: 700;
          color: ${colors.BLACK};
          letter-spacing: 0.2px;
          text-transform: uppercase;
        }

        @media (min-width: ${screen.smMin}) {
          .price-history-subtitle {
            font-size: 20px;
          }
        }

        .days-flex-container {
          display: flex;
          align-items: center;
        }

        .clock-icon {
          display: block;
          width: 18px;
          height: 18px;
          background-image: url(${clockIcon});
          background-size: cover;
          margin-right: 6px;
        }

        .days {
          margin-right: 4px;
          font-size: 18px;
          font-weight: 700;
        }

        @media (min-width: ${screen.smMin}) {
          .days {
            font-size: 20px;
          }
        }

        .on-market-text {
          text-transform: lowercase;
          font-size: 14px;
          font-weight: 400;
        }

        @media (min-width: ${screen.smMin}) {
          .on-market-text {
            font-size: 16px;
          }
        }

        .new-listing {
          font-size: 16px;
          color: ${colors.ALG2};
          text-transform: uppercase;
          font-weight: 600;
        }

        .total-price-change {
          font-size: 16px;
          color: ${colors.BLACK};
          font-weight: 400;
          margin: 4px 0px 8px 0px;
        }

        .red {
          color: ${colors.ALR1};
        }

        .green {
          color: ${colors.ALG2};
        }

        .black {
          color: ${colors.BLACK};
        }

        .price-history-timeline {
          display: flex;
          width: 100%;
          padding-left: 7px;
        }

        .timeline {
          background-color: ${colors.G2};
          width: 2px;
          margin: 26px 16px 28px 0px;
        }

        @media (min-width: ${screen.smMin}) {
          .timeline {
            width: 2px;
            margin: 26px 24.5px 31px 0px;
          }
        }
      `}</style>
      {linkButtonCss.styles}
    </div>
  );
};

export default PriceHistory;
